import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, FlaskConical, Search, Download } from "lucide-react";
import { useHubClinicalTrials } from "@/hooks/useHubResearch";

interface DrugPipelineTabProps {
  accountId: string;
  companyName: string;
  company: Record<string, any>;
  hubAccountName: string | null;
}

function getMoleculeTypeBadge(modality: string | null) {
  if (!modality) return null;
  const colors: Record<string, string> = {
    "Cell Therapy": "bg-green-600 text-white",
    "Gene Therapy": "bg-blue-600 text-white",
    "CAR-T": "bg-purple-600 text-white",
    "CAR-NK": "bg-pink-600 text-white",
    "MSC": "bg-teal-600 text-white",
    "AAV": "bg-indigo-600 text-white",
  };
  const color = Object.entries(colors).find(([key]) => modality.toLowerCase().includes(key.toLowerCase()));
  return (
    <Badge className={`text-xs ${color ? color[1] : "bg-gray-600 text-white"}`}>
      {modality}
    </Badge>
  );
}

function getStageBadge(phase: string | null) {
  if (!phase) return <span className="text-muted-foreground">—</span>;
  const colors: Record<string, string> = {
    "Phase 1": "bg-blue-100 text-blue-800 border-blue-200",
    "Phase 2": "bg-purple-100 text-purple-800 border-purple-200",
    "Phase 3": "bg-green-100 text-green-800 border-green-200",
    "Phase 4": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Preclinical": "bg-gray-100 text-gray-800 border-gray-200",
    "Discovery": "bg-yellow-100 text-yellow-800 border-yellow-200",
  };
  const color = Object.entries(colors).find(([key]) => phase.includes(key));
  return <Badge variant="outline" className={`text-xs ${color ? color[1] : "bg-gray-100 text-gray-700 border-gray-200"}`}>{phase}</Badge>;
}

export function DrugPipelineTab({ accountId, companyName, company, hubAccountName }: DrugPipelineTabProps) {
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");

  const { data: trials, isLoading } = useHubClinicalTrials(companyName, hubAccountName);

  // Build drug pipeline from CARR company data + Hub trials
  const drugs = useMemo(() => {
    const drugList: {
      name: string;
      genericName: string | null;
      moleculeType: string | null;
      developmentStage: string | null;
      clinicalPhase: string | null;
      confidence: number;
      trialCount: number;
      isFavorite: boolean;
    }[] = [];

    // Parse drug names from CARR data
    if (company.carr_drug_names) {
      const names = company.carr_drug_names.split(/[,;]/).map((n: string) => n.trim()).filter(Boolean);
      names.forEach((name: string) => {
        const matchingTrials = trials?.filter(t =>
          t.title?.toLowerCase().includes(name.toLowerCase()) ||
          t.interventions?.toString().toLowerCase().includes(name.toLowerCase())
        ) || [];
        const highestPhase = matchingTrials.reduce((best: string | null, t) => {
          if (!t.phase) return best;
          if (!best) return t.phase;
          const phaseNum = (p: string) => {
            if (p.includes("3")) return 3;
            if (p.includes("2")) return 2;
            if (p.includes("1")) return 1;
            return 0;
          };
          return phaseNum(t.phase) > phaseNum(best) ? t.phase : best;
        }, null);

        drugList.push({
          name,
          genericName: null,
          moleculeType: company.carr_modality || null,
          developmentStage: highestPhase || company.carr_highest_phase || null,
          clinicalPhase: highestPhase || null,
          confidence: matchingTrials.length > 0 ? 100 : 1,
          trialCount: matchingTrials.length,
          isFavorite: true,
        });
      });
    }

    // If no drugs from CARR data, try to infer from trials
    if (drugList.length === 0 && trials && trials.length > 0) {
      // Group trials by intervention
      const seen = new Set<string>();
      trials.forEach(t => {
        const interventionName = t.title?.split(":")[0]?.trim() || t.nct_id;
        if (!seen.has(interventionName)) {
          seen.add(interventionName);
          drugList.push({
            name: interventionName,
            genericName: null,
            moleculeType: t.modality || company.carr_modality || null,
            developmentStage: t.phase || null,
            clinicalPhase: t.phase || null,
            confidence: 1,
            trialCount: 1,
            isFavorite: false,
          });
        }
      });
    }

    return drugList;
  }, [company, trials]);

  // Filter drugs
  const filteredDrugs = useMemo(() => {
    return drugs.filter(d => {
      if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (stageFilter !== "all" && d.developmentStage !== stageFilter) return false;
      if (typeFilter !== "all" && d.moleculeType !== typeFilter) return false;
      if (phaseFilter !== "all" && d.clinicalPhase !== phaseFilter) return false;
      return true;
    });
  }, [drugs, search, stageFilter, typeFilter, phaseFilter]);

  // Summary stats
  // NOTE: "Approved" stat card removed — clinical trials data only exposes
  // Phase 1-4, never an "Approved" string, so the count was structurally
  // always 0. Re-add once a real FDA approval data source (Orange Book /
  // DrugBank) is wired into the Hub enrichment pipeline.
  const totalDrugs = drugs.length;
  const inDevelopment = drugs.filter(d => d.developmentStage).length;
  const withTrials = drugs.filter(d => d.trialCount > 0).length;

  if (isLoading) return <div className="flex items-center justify-center h-[300px]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6 p-6">
      {/* Overview Cards */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Drug Pipeline Overview</h3>
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-blue-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{totalDrugs}</p>
              <p className="text-sm text-muted-foreground">Total Drugs</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">{inDevelopment}</p>
              <p className="text-sm text-muted-foreground">In Development</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{withTrials}</p>
              <p className="text-sm text-muted-foreground">With Trials</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Drug Pipeline Details */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Drug Pipeline Details</CardTitle>
            <Button variant="outline" size="sm" className="gap-2">
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by drug name..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Stages" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                <SelectItem value="Discovery">Discovery</SelectItem>
                <SelectItem value="Preclinical">Preclinical</SelectItem>
                <SelectItem value="Phase 1">Phase 1</SelectItem>
                <SelectItem value="Phase 2">Phase 2</SelectItem>
                <SelectItem value="Phase 3">Phase 3</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Cell Therapy">Cell Therapy</SelectItem>
                <SelectItem value="Gene Therapy">Gene Therapy</SelectItem>
                <SelectItem value="CAR-T">CAR-T</SelectItem>
                <SelectItem value="MSC">MSC</SelectItem>
              </SelectContent>
            </Select>
            <Select value={phaseFilter} onValueChange={setPhaseFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Phases" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Phases</SelectItem>
                <SelectItem value="Phase 1">Phase 1</SelectItem>
                <SelectItem value="Phase 2">Phase 2</SelectItem>
                <SelectItem value="Phase 3">Phase 3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="text-sm text-muted-foreground">Showing {filteredDrugs.length} of {totalDrugs} drugs</p>

          {/* Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Drug Name</TableHead>
                <TableHead>Generic/Brand Names</TableHead>
                <TableHead>Molecule Type</TableHead>
                <TableHead>Development Stage</TableHead>
                <TableHead>Clinical Phase</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Trials</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDrugs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <FlaskConical className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No drug pipeline data available</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDrugs.map((drug, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {drug.isFavorite && <span className="text-yellow-500">★</span>}
                        <span className="font-medium">{drug.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{drug.genericName || "—"}</TableCell>
                    <TableCell>{getMoleculeTypeBadge(drug.moleculeType)}</TableCell>
                    <TableCell>{getStageBadge(drug.developmentStage)}</TableCell>
                    <TableCell className="text-muted-foreground">{drug.clinicalPhase || "—"}</TableCell>
                    <TableCell>
                      <span className={`text-sm font-medium ${drug.confidence === 100 ? "text-green-600" : "text-muted-foreground"}`}>
                        {drug.confidence}%
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">{drug.trialCount}</Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
